import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { MapPin, TrendingUp, Mail, UserCheck } from 'lucide-react';

const Candidates = () => {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const response = await axios.get(`${API_BASE}/candidates`, {
        params: { employer_id: user.id }
      });
      setCandidates(response.data);
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (candidateId) => {
    try {
      await axios.post(`${API_BASE}/candidates/action`, {
        employer_id: user.id,
        candidate_id: candidateId,
        action: 'invite'
      });
      toast.success('Interview invitation sent!');
      fetchCandidates();
    } catch (error) {
      toast.error('Failed to send invitation');
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D9488]"></div></div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Eligible Candidates</h1>
        <p className="text-lg text-slate-600 mb-8">{candidates.length} candidates matched to your roles</p>

        {candidates.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <p className="text-slate-500">No candidates available. Post a role to start matching!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {candidates.map((candidate) => (
              <div
                key={candidate.id}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-all"
                data-testid={`candidate-${candidate.apprentice_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {candidate.profile?.name || 'Apprentice'}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        candidate.match_score >= 80
                          ? 'bg-green-100 text-green-700'
                          : candidate.match_score >= 60
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {candidate.match_score}% Match
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-slate-600 mb-4">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {candidate.profile?.location}
                      </div>
                      <div className="flex items-center">
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Readiness: {candidate.profile?.readiness_score}/100
                      </div>
                    </div>
                    <div className="mb-4">
                      <h4 className="font-semibold text-sm text-slate-700 mb-2">Why Matched:</h4>
                      <p className="text-sm text-slate-600">{candidate.why_matched}</p>
                    </div>
                    {candidate.cv?.summary && (
                      <div>
                        <h4 className="font-semibold text-sm text-slate-700 mb-2">Summary:</h4>
                        <p className="text-sm text-slate-600">{candidate.cv.summary}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 ml-6">
                    <Button
                      onClick={() => handleInvite(candidate.apprentice_id)}
                      className="bg-[#0D9488] hover:bg-[#115E59] rounded-full"
                      data-testid={`invite-candidate-${candidate.apprentice_id}-btn`}
                    >
                      <UserCheck className="mr-2 w-4 h-4" />
                      Invite to Interview
                    </Button>
                    <Button variant="secondary" className="rounded-full">
                      <Mail className="mr-2 w-4 h-4" />
                      Message
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Candidates;
